import { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';

interface Props {
  onScan: (text: string) => void;
  paused?: boolean;
}

// Сканер камеры через @zxing/browser. Поддерживает Data Matrix, EAN-13, Code 128.
// Если в браузере несколько камер — приоритет задняя (environment).
export function BarcodeScanner({ onScan, paused = false }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    if (paused) {
      controlsRef.current?.stop();
      controlsRef.current = null;
      setScanning(false);
      return;
    }
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.DATA_MATRIX,
      BarcodeFormat.CODE_128,
      BarcodeFormat.EAN_13,
      BarcodeFormat.QR_CODE,
    ]);
    const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 100 });

    let cancelled = false;
    (async () => {
      try {
        const devices = await BrowserMultiFormatReader.listVideoInputDevices();
        const back = devices.find((d) => /back|rear|environment/i.test(d.label)) ?? devices[devices.length - 1];
        if (!back) throw new Error('No camera found');
        if (cancelled || !videoRef.current) return;
        const controls = await reader.decodeFromVideoDevice(back.deviceId, videoRef.current, (result) => {
          if (result && !paused) {
            onScan(result.getText());
          }
        });
        if (cancelled) { controls.stop(); return; }
        controlsRef.current = controls;
        setScanning(true);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
      controlsRef.current?.stop();
      controlsRef.current = null;
    };
  }, [paused, onScan]);

  return (
    <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', background: '#000' }}>
      <video ref={videoRef} style={{ width: '100%', maxWidth: 480, display: 'block' }} muted playsInline />
      {!scanning && !error && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#fff' }}>Инициализация камеры…</div>}
      {error && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,0.7)', color: 'var(--red)', padding: 16, textAlign: 'center', fontSize: 13 }}>{error}<br />Используйте HTTPS и разрешите доступ к камере.</div>}
    </div>
  );
}
